import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';

import { BACKEND_URL } from '../config/api';

export default function SharedCaseClaim() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [patientId, setPatientId] = useState(null);

  useEffect(() => {
    const claim = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/cases/share-link/${token}/claim`, { withCredentials: true });
        setPatientId(data.patient_id);
        if (data.already_owner) {
          setStatus('owner');
          setMessage('This is your own patient.');
        } else if (data.already_shared) {
          setStatus('exists');
          setMessage('You already have access to this patient.');
        } else {
          setStatus('success');
          setMessage(data.message);
        }
      } catch (err) {
        const code = err.response?.status;
        if (code === 410) {
          setStatus('expired');
          setMessage('This share link has expired.');
        } else if (code === 404) {
          setStatus('invalid');
          setMessage('This share link is invalid or has been revoked.');
        } else {
          setStatus('error');
          setMessage('Something went wrong. Please try again.');
        }
      }
    };
    claim();
  }, [token]);

  const icon = {
    loading: <Loader2 className="w-12 h-12 animate-spin text-[#4B6651]" />,
    success: <CheckCircle className="w-12 h-12 text-[#4B6651]" />,
    owner: <CheckCircle className="w-12 h-12 text-[#4B6651]" />,
    exists: <CheckCircle className="w-12 h-12 text-[#4B6651]" />,
    expired: <Clock className="w-12 h-12 text-[#C06144]" />,
    invalid: <XCircle className="w-12 h-12 text-[#D94F4F]" />,
    error: <XCircle className="w-12 h-12 text-[#D94F4F]" />,
  }[status];

  const canView = ['success', 'owner', 'exists'].includes(status);

  return (
    <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center p-4" data-testid="shared-claim-page">
      <div className="bg-white rounded-xl shadow-sm border border-[#E5E4E0] p-8 max-w-md w-full text-center space-y-4">
        {icon}
        {status === 'loading' ? (
          <p className="text-[#5C5F5C]">Claiming shared access...</p>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-[#2A2C2A]">
              {(() => {
                const titles = { success: 'Access Granted', expired: 'Link Expired', invalid: 'Invalid Link', error: 'Error' };
                return titles[status] || 'Patient Access';
              })()}
            </h2>
            <p className="text-[#5C5F5C]">{message}</p>
            <div className="flex gap-3 justify-center pt-2">
              {canView && (
                <Button
                  onClick={() => navigate(`/cases/${patientId}`)}
                  className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
                  data-testid="view-shared-case-btn"
                >
                  View Case
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-[#E5E4E0]"
                data-testid="go-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
